/**
 * sl-suoi.3.1: Folder Doc Prefetch Service — Folder Metadata Prefetch
 *
 * Observes folder expand/collapse state and primes permission-filtered
 * metadata for docs in visible folders. Document content remains on-demand;
 * listing or expanding a folder must never enqueue every child Y.Doc.
 *
 * SOLID:
 * - S: Only handles folder-driven doc prefetch orchestration
 * - D: Depends on abstractions (WorkspaceService, FolderStore, AccessibleDocCacheStore)
 */
import { DebugLogger } from '@affine/debug';
import { OnEvent, Service } from '@toeverything/infra';
import { Subscription } from 'rxjs';

import { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import type { Workspace } from '../../workspace/entities/workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import { WorkspaceService } from '../../workspace/services/workspace';
import { isUserVisibleDocId } from '../../workspace/utils/user-visible-docs';
import { FolderStore } from '../stores/folder';

const logger = new DebugLogger('FolderDocPrefetch');

@OnEvent(WorkspaceInitialized, s => s.onWorkspaceInitialized)
export class FolderDocPrefetchService extends Service {
  private readonly folderSubscriptions = new Map<string, Subscription>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly folderStore: FolderStore,
    private readonly accessibleDocCache: AccessibleDocCacheStore
  ) {
    super();
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      return; // Local workspaces use eager sync
    }

    // Prefetch root-level folder docs on connect
    this.prefetchFolderDocs(null, true);
  }

  /**
   * Called when a folder is expanded in the sidebar.
   * Triggers doc content sync for docs in this folder + N+1 prefetch.
   */
  onFolderExpanded(folderId: string): void {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      return;
    }

    // Prefetch docs in the expanded folder
    this.prefetchFolderDocs(folderId, true);
  }

  /**
   * Called when a folder is collapsed — clean up subscriptions.
   */
  onFolderCollapsed(folderId: string): void {
    // Clean up sub-folder subscriptions to prevent leaks
    const sub = this.folderSubscriptions.get(folderId);
    if (sub) {
      sub.unsubscribe();
      this.folderSubscriptions.delete(folderId);
    }
  }

  override dispose(): void {
    for (const sub of this.folderSubscriptions.values()) {
      sub.unsubscribe();
    }
    this.folderSubscriptions.clear();
    super.dispose();
  }

  /**
   * Add all docs in a folder to the sync queue and prime their permission-filtered
   * metadata so folder doc links can render from the local doc cache immediately.
   */
  private prefetchFolderDocs(
    parentId: string | null,
    includeChildFolders = false
  ): void {
    const workspace = this.workspaceService.workspace;
    const sub = this.folderStore
      .watchNodeChildren(parentId)
      .subscribe(children => {
        const docIds: string[] = [];
        for (const child of children) {
          if (
            child.type === 'doc' &&
            isUserVisibleDocId(child.data, workspace.id)
          ) {
            docIds.push(child.data);
          }
        }

        if (docIds.length > 0) {
          this.accessibleDocCache.fetchFolderDocs(docIds).catch(() => {
            // Metadata fetch is opportunistic and remains local-first.
          });
          logger.debug(
            `Prefetched metadata for ${docIds.length} docs in folder ${parentId ?? 'root'}`
          );
        }

        if (includeChildFolders) {
          for (const child of children) {
            if (child.type === 'folder') {
              this.prefetchFolderDocs(child.id);
            }
          }
        }
      });

    // Store subscription keyed by parent ID for cleanup
    const key = parentId ?? '__root__';
    const existing = this.folderSubscriptions.get(key);
    if (existing) {
      existing.unsubscribe();
    }
    this.folderSubscriptions.set(key, sub);
  }
}
